import * as Yup from 'yup';
import { matchIsValidTel } from 'mui-tel-input';
import { URL_OR_DOMAIN_REGEX } from 'types/suppliers';

export const formConstants = {
  supplier: { id: 'supplier', label: 'Supplier' },
  name: { id: 'name', label: 'Supplier Name' },
  alsoCreatePublicSupplier: {
    id: 'alsoCreatePublicSupplier',
    label: 'Also Create Public Supplier',
    description: 'Cofactr Staff Only',
  },
  connectionType: { id: 'connectionType', label: 'Default Connection Type' },
  website: { id: 'website', label: 'Website' },
  contactName: { id: 'contactName', label: 'Contact Name' },
  phone: { id: 'phone', label: 'Phone' },
  orderEmail: {
    id: 'orderEmail',
    label: 'Order Email',
    description:
      'New Purchase Orders and PO status inquires will be sent to this email.',
  },
  quotingEmail: {
    id: 'quotingEmail',
    label: 'Quoting Email',
    description: 'Requests for quotes will be sent to this email.',
  },
  paymentEmail: {
    id: 'paymentEmail',
    label: 'Payment Email',
    description:
      'Remittance and other payment related information will be sent to this email.',
  },
  customReelPrice: {
    id: 'customReelPrice',
    label: 'Custom Reel Price',
  },
  minimumLineSize: {
    id: 'minimumLineSize',
    label: 'Minimum Price Per Line',
    description:
      'This is the minimum allowable dollar value of each line on a Purchase Order to this supplier.',
  },
  minimumOrderSize: {
    id: 'minimumOrderSize',
    label: 'Minimum Order Total',
    description:
      'This is the minimum allowable total for a Purchase Order to this supplier.',
  },
  minimumOrderForScheduledRelease: {
    id: 'minimumOrderForScheduledRelease',
    label: 'Minimum Scheduled Release Order Total',
    description:
      'This is the minimum allowable total for a Purchase Order to this supplier when one or more lines has a scheduled release specified.',
  },
  minimumLineForScheduledRelease: {
    id: 'minimumLineForScheduledRelease',
    label: 'Minimum Scheduled Release Price Per Line',
    description:
      'This is the minimum allowable dollar value of each line on a Purchase Order to this supplier when that line has a scheduled release specified.',
  },
  shippingOptions: {
    id: 'shippingOptions',
    label: 'Shipping Options',
  },
  freeShipThreshold: {
    id: 'freeShipThreshold',
    label: 'Free Shipping - Minimum Order',
  },
  freeShipShippingLead: {
    id: 'freeShipShippingLead',
    label: 'Free Shipping - Ship Speed (Business Days)',
  },
  shippingPriceIsEstimate: {
    id: 'shippingPriceIsEstimate',
    label: 'Shipping Options are Estimates',
    description:
      'Indicates that shipping prices for this supplier are estimates that may vary order-to-order. This is used for alerts in purchasing workflows.',
  },
  bestCaseLead: {
    id: 'bestCaseLead',
    label: 'Best Case Lead (Business Days)',
    description:
      'The fastest lead time in business days from this supplier. This is used for lead time estimates in purchasing workflows.',
  },
  shipsInLead: {
    id: 'shipsInLead',
    label: 'Ships In Lead (Business Days)',
    description:
      'The typical time in business days between placing an order with this supplier and shipment. This is used for lead time estimates in purchasing workflows.',
  },
  uncertainLead: {
    id: 'uncertainLead',
    label: 'Uncertain Lead',
    description:
      'Indicates that lead times from this supplier are often variable or do not match estimates. This is used for lead time warnings in purchasing workflows.',
  },
  scheduledReleasePeriod: {
    id: 'scheduledReleasePeriod',
    label: 'Scheduled Release Period (Months)',
    description:
      'The maximum time in months between placing and order and accepting delivery when using scheduled releases with this supplier.',
  },
  convenienceReturnWindow: {
    id: 'convenienceReturnWindow',
    label: 'Convenience Return Period (Days)',
    description:
      'The maximum time in days after an order is shipped that this supplier will accept a return, other than for defects.',
  },
  usePricingApi: {
    id: 'usePricingApi',
    label: 'Use Pricing API',
    description:
      'Custom pricing will automatically be fetched from this supplier via their API.',
  },
  useOrderSubmissionApi: {
    id: 'useOrderSubmissionApi',
    label: 'Use Order Submission API',
    description: 'Purchase Orders to this supplier will be sent via their API.',
  },
  useOrderStatusApi: {
    id: 'useOrderStatusApi',
    label: 'Use Order Status API',
    description: 'Purchase order status data will be fetched via their API.',
  },
  useOrderPo: {
    id: 'useOrderPo',
    label: 'Send Orders via Email',
    description:
      'Purchase Orders to this supplier will be sent as PDFs via email.',
  },
  useOnlineCheckout: {
    id: 'useOnlineCheckout',
    label: 'Place Orders via Website',
    description:
      'Purchase Orders to this supplier will be manually processed via their website',
  },
  useCustomReeling: {
    id: 'useCustomReeling',
    label: 'Use Custom Reeling',
    description:
      'Parts purchased from this supplier should be provided on custom reels whenever that option is available.',
  },
  allowNonPartPurchaseLines: {
    id: 'allowNonPartPurchaseLines',
    label: 'Allow Other Lines',
    description:
      'This supplier accepts Purchase Orders containing Other (non-part) Lines',
  },
  automaticallyGeneratePurchases: {
    id: 'automaticallyGeneratePurchases',
    label: 'Automatically Generate Purchase Orders',
    description:
      'Purchase Orders will automatically be generated when new Requisitions are created for this supplier.',
  },
  automaticallyAddRequestsToOpenPurchases: {
    id: 'automaticallyAddRequestsToOpenPurchases',
    label: 'Automatically Add Requests to Draft Purchase Orders',
    description:
      'Requisitions created for this supplier will automatically be added to the first matching draft Purchase Order.',
  },
  automaticallyApprovePurchases: {
    id: 'automaticallyApprovePurchases',
    label: 'Automatically Approve Purchase Orders',
    description:
      'Purchase Orders created with lines for this supplier will automatically be approved upon creation.',
  },
  automaticallySendPurchases: {
    id: 'automaticallySendPurchases',
    label: 'Automatically Send Purchase Orders',
    description:
      'Purchase Orders for this supplier will automatically be sent when they are approval.',
  },
  allowDropship: {
    id: 'allowDropship',
    label: 'Allow Drop-Ships',
  },
  poFooterNotes: {
    id: 'poFooterNotes',
    label: 'Default PO Footer Text',
    description:
      'The default text that will be displayed on new PDF purchase orders generated in Cofactr for this supplier',
  },
  defaultPoTemplate: {
    id: 'defaultPoTemplate',
    label: 'Default PO Template',
  },
  invoiceApproval: {
    id: 'invoiceApproval',
    label: 'Supplier Bill Auto-Approval Rule',
    description:
      'This determines when Supplier Bills for this supplier will be auto-approved.',
  },
  updatePurchaseLinePricesFromBill: {
    id: 'updatePurchaseLinePricesFromBill',
    label: 'Update Purchase Line Prices from Bill',
    description:
      'If enabled, price discrepancies between the PO line and Supplier Bill line will cause the PO to be automatically updated to match the Supplier Bill.',
  },
  ignoreShippingInBillApproval: {
    id: 'ignoreShippingInBillApproval',
    label: 'Ignore Shipping in Bill Approval',
    description:
      'If enabled, shipping price discrepancies between the PO and Supplier Bill will be ignored in the bill approval process.',
  },
  updatePurchaseShippingFromBill: {
    id: 'updatePurchaseShippingFromBill',
    label: 'Update Purchase Shipping from Bill',
    description:
      'If enabled, shipping price discrepancies between the PO and Supplier Bill will cause the PO to be automatically updated to match the Supplier Bill.',
  },
  ignoreDutiesInBillApproval: {
    id: 'ignoreDutiesInBillApproval',
    label: 'Ignore Duties in Bill Approval',
    description:
      'If enabled, duty price discrepancies between the PO and Supplier Bill will be ignored in the bill approval process.',
  },
  updatePurchaseDutiesFromBill: {
    id: 'updatePurchaseDutiesFromBill',
    label: 'Update Purchase Duties from Bill',
    description:
      'If enabled, duty price discrepancies between the PO and Supplier Bill will cause the PO to be automatically updated to match the Supplier Bill.',
  },
  ignoreTaxesInBillApproval: {
    id: 'ignoreTaxesInBillApproval',
    label: 'Ignore Taxes in Bill Approval',
    description:
      'If enabled, tax price discrepancies between the PO and Supplier Bill will be ignored in the bill approval process.',
  },
  updatePurchaseTaxesFromBill: {
    id: 'updatePurchaseTaxesFromBill',
    label: 'Update Purchase Taxes from Bill',
    description:
      'If enabled, tax price discrepancies between the PO and Supplier Bill will cause the PO to be automatically updated to match the Supplier Bill.',
  },
  onlyUseTotalForBillApproval: {
    id: 'onlyUseTotalForBillApproval',
    label: 'Only Use Total for Bill Approval',
    description:
      'If enabled, only the total price of the Supplier Bill will be used to determine if the PO should be auto-approved. Line-level matching will be ignored.',
  },
  autoScrape: {
    id: 'autoScrape',
    label: 'Auto Ailana Check',
    description:
      'If enabled, Ailana will automatically check the parts from this supplier to fill in missing data and fetch supplier pricing.',
  },
  rescrapeInterval: {
    id: 'rescrapeInterval',
    label: 'Re-Check Interval (Days)',
    description:
      'The interval in days between automatically re-checking parts for this supplier.',
  },
  manufacturerMatchRegex: {
    id: 'manufacturerMatchRegex',
    label: 'Manufacturer Match Regex',
    description:
      'The regex to use to match part Manufacturer values to this Supplier.',
  },
  mpnMatchRegex: {
    id: 'mpnMatchRegex',
    label: 'MPN Match Regex',
    description: 'The regex to use to match part MPN values to this Supplier.',
  },
  productPatchUrl: {
    id: 'productPatchUrl',
    label: 'Product Page URL',
    description:
      'The supplier URL to scrape the product from. Include an {{mpn}} placeholder for the MPN.',
  },
};

export const coreValidationSchema = {
  [formConstants.name.id]: Yup.string(),
  [formConstants.website.id]: Yup.string()
    .trim()
    .matches(URL_OR_DOMAIN_REGEX, 'Website must be a domain or full URL')
    .nullable(),
  [formConstants.contactName.id]: Yup.string().nullable(),
  [formConstants.phone.id]: Yup.string()
    .max(32, 'Phone number must be under 32 characters')
    .nullable()
    .test('is-valid-phone', 'Must be a valid phone number.', (value) => {
      if (!value) return true;
      return matchIsValidTel(value, 'US');
    }),
  [formConstants.orderEmail.id]: Yup.string()
    .email('Must be a valid email address')
    .nullable()
    .typeError('Must be a valid email address'),
  [formConstants.quotingEmail.id]: Yup.string()
    .email('Must be a valid email address')
    .nullable()
    .typeError('Must be a valid email address'),
  [formConstants.paymentEmail.id]: Yup.string()
    .email('Must be a valid email address')
    .nullable()
    .typeError('Must be a valid email address'),
  [formConstants.customReelPrice.id]: Yup.number()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid number'),
  [formConstants.minimumLineSize.id]: Yup.number()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid number'),
  [formConstants.minimumOrderSize.id]: Yup.number()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid number'),
  [formConstants.minimumOrderForScheduledRelease.id]: Yup.number()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid number'),
  [formConstants.minimumLineForScheduledRelease.id]: Yup.number()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid number'),
  [formConstants.shippingPriceIsEstimate.id]: Yup.boolean().nullable(),
  [formConstants.bestCaseLead.id]: Yup.number()
    .integer()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid integer'),
  [formConstants.scheduledReleasePeriod.id]: Yup.number()
    .integer()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid integer'),
  [formConstants.convenienceReturnWindow.id]: Yup.number()
    .integer()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid integer'),
  [formConstants.shipsInLead.id]: Yup.number()
    .integer()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid integer'),
  [formConstants.freeShipShippingLead.id]: Yup.number()
    .integer()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid integer')
    .when(formConstants.freeShipThreshold.id, {
      is: (threshold: number) => threshold !== null && threshold !== undefined,
      then: (schema) =>
        schema.required(
          'Free shipping lead time is required when free shipping threshold is set'
        ),
      otherwise: (schema) => schema.nullable(),
    }),
  [formConstants.freeShipThreshold.id]: Yup.number()
    .nullable()
    .min(0, 'Must be 0 or greater')
    .typeError('Must be a valid integer'),
  [formConstants.uncertainLead.id]: Yup.boolean().nullable(),
  [formConstants.usePricingApi.id]: Yup.boolean().nullable(),
  [formConstants.useOrderSubmissionApi.id]: Yup.boolean().nullable(),
  [formConstants.useOrderStatusApi.id]: Yup.boolean().nullable(),
  [formConstants.useOrderPo.id]: Yup.boolean().nullable(),
  [formConstants.useOnlineCheckout.id]: Yup.boolean().nullable(),
  [formConstants.useCustomReeling.id]: Yup.boolean().nullable(),
  [formConstants.allowNonPartPurchaseLines.id]: Yup.boolean().nullable(),
  [formConstants.automaticallyGeneratePurchases.id]: Yup.boolean().nullable(),
  [formConstants.automaticallyAddRequestsToOpenPurchases.id]:
    Yup.boolean().nullable(),
  [formConstants.automaticallyApprovePurchases.id]: Yup.boolean().nullable(),
  [formConstants.automaticallySendPurchases.id]: Yup.boolean().nullable(),
  [formConstants.poFooterNotes.id]: Yup.string().nullable(),
  [formConstants.defaultPoTemplate.id]: Yup.string().nullable(),
};

export const createValidationSchema = Yup.object().shape({
  [formConstants.supplier.id]: Yup.object()
    .shape({
      id: Yup.string()
        .required('Supplier is required')
        .typeError('Supplier is required'),
      name: Yup.string()
        .max(255, 'Supplier must be under 255 characters')
        .required('Supplier is required')
        .typeError('Supplier is required'),
    })
    .typeError('Supplier is required'),
  [formConstants.alsoCreatePublicSupplier.id]: Yup.boolean(),
  ...coreValidationSchema,
});

export const updateValidationSchema = Yup.object().shape({
  ...coreValidationSchema,
});

export const getCopy = (isEdit: boolean) => ({
  title: isEdit ? 'Edit Supplier' : 'Create Supplier',
  submit: isEdit ? 'Save' : 'Submit',
  success: isEdit
    ? 'Supplier successfully updated'
    : 'Supplier successfully created',
});
